import { normalizeToSupportedLocale } from "./locale";
import { type SupportedLanguage } from "./resources";

/**
 * Storage key for persisting language preference in localStorage.
 */
const LANGUAGE_STORAGE_KEY = "modernloop.language";

/**
 * Gets the saved language from localStorage.
 * Normalizes legacy one-part locale codes to two-part format.
 *
 * @returns The saved language code (normalized to two-part format) or null if not set.
 */
export const getSavedLanguage = (): SupportedLanguage | null => {
  const browserWindow = globalThis.window;
  if (browserWindow === undefined || !browserWindow.localStorage) {
    return null;
  }

  let savedValue: string | null = null;
  try {
    savedValue = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // localStorage read might be blocked in some browsers
    return null;
  }

  if (!savedValue) {
    return null;
  }

  // Normalize legacy values to two-part format
  const normalized = normalizeToSupportedLocale(savedValue);

  // If the saved value was in legacy format, attempt to update it
  // Write failure should not discard the valid normalized value
  if (normalized && normalized !== savedValue) {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    } catch {
      // localStorage write might fail (quota exceeded, private browsing, etc.)
      // Continue with the valid normalized value
    }
  }

  return normalized;
};

/**
 * Saves the language preference to localStorage.
 * @param language - The language code to save (e.g., 'en-US', 'fr-FR').
 */
export const saveLanguage = (language: string): void => {
  const browserWindow = globalThis.window;
  if (browserWindow === undefined || !browserWindow.localStorage) {
    return;
  }
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // localStorage might be blocked in some browsers
  }
};

/**
 * Detects the browser's preferred language and normalizes it to a supported locale.
 * Handles various browser locale formats including BCP-47 tags.
 *
 * @returns The best matching supported language code or null if no match.
 *
 * @example
 * // Browser language: 'en-US' → 'en-US'
 * // Browser language: 'en-GB' → 'en-US'
 * // Browser language: 'zh-Hant-TW' → 'zh-TW'
 * // Browser language: 'de-DE' → null (unsupported)
 */
export const detectBrowserLanguage = (): SupportedLanguage | null => {
  const browserNavigator = globalThis.navigator;
  if (browserNavigator === undefined) {
    return null;
  }

  const browserLang = browserNavigator.language;

  if (!browserLang) {
    return null;
  }

  // Normalize the full browser language string to a supported locale
  return normalizeToSupportedLocale(browserLang);
};
